import type { ConvertBracketToDot } from '#types/convert-bracket-to-dot.type';
import type { GetPathValue } from '#types/get-path-value.type';
import type { NestedPath } from '#types/nested-path.type';

import { isEmpty } from '#misc/is-empty';
import { isNotDefined } from '#misc/is-not-defined.guard';
import { isNumber } from '#number/is-number';
import { isObject } from '#object/is-object';

import { replacePath } from './utilities/replace-path';

/**
 * Retrieves the value at a given nested path within an object, supporting both dot and bracket notation.
 * If the path does not exist or the value is undefined, returns the provided default value (if any).
 *
 * @typeParam T - The type of the source object.
 * @typeParam P - The type of the path, constrained to valid nested paths of T.
 * @typeParam D - The type of the default value.
 * @param value - The source object from which to retrieve the value.
 * @param path - The path to the desired property, using dot or bracket notation (e.g., 'a.b[0].c').
 * @param defaultValue - The value to return if the path does not exist or is undefined.
 * @returns The value at the specified path, or the default value if not found.
 *
 * @example
 * ```typescript
 * const obj = { a: { b: [{ c: 42 }] } };
 * const result = get(obj, 'a.b[0].c'); // result === 42
 * const missing = get(obj, 'a.b[1].c', 'not found'); // missing === 'not found'
 * ```
 */
export function get<T extends object, P extends NestedPath<T>>(
  value: T,
  path: P
): GetPathValue<T, ConvertBracketToDot<P>> | undefined;
export function get<T extends object, P extends NestedPath<T>, D>(
  value: T,
  path: P,
  defaultValue: D
): D | GetPathValue<T, ConvertBracketToDot<P>>;
export function get<T extends object, P extends NestedPath<T>, D = undefined>(
  value: T,
  path: P,
  defaultValue?: D
): D | GetPathValue<T, ConvertBracketToDot<P>> | undefined {
  if (isNotDefined(value) || typeof path !== 'string' || isEmpty(path)) {
    return defaultValue;
  }

  const parts = replacePath(path).split('.');
  let result: unknown = value;

  for (const key of parts) {
    if (isNotDefined(result)) {
      return defaultValue;
    }

    if (isObject(result)) {
      const obj = result;

      result = obj[key] ?? defaultValue;

      continue;
    }

    if (Array.isArray(result) && isNumber(key)) {
      const index = Number(key);
      const arr = result as unknown[];

      result = arr[index] ?? defaultValue;

      continue;
    }

    result = defaultValue;

    break;
  }

  return result as D | GetPathValue<T, ConvertBracketToDot<P>> | undefined;
}
